import get from "lodash/get";
import React, { useEffect, useMemo, useState } from "react";
import { Button, DownloadIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";

import Preloader from "~/components/Preloader";
import RegMenFilter from "~/pages/RegMen/Filter";
import Wrapper from "~/shared/ui/Wrapper";
import {
  useGetRegMenByPeriodReport,
  useGetRegMenByPeriodReportFile,
  useGetRegMenConsolidateReport,
  useGetRegMenConsolidateReportFile,
} from "~/entity/RegMenReports";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  useGetReportFileRegMenByDealerByDealerLazyQuery,
  useGetReportRegMenByDealerByDealerLazyQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { DefaultTextCell, TableV2 } from "~/shared/ui/TableV2";

import PeriodFilter from "./PeriodFilter";
import {
  mapConsolidateRows,
  mapPeriodRows,
  PERIOD_BASE_OPTIONS,
  PERIOD_DIRECTION_COLUMNS,
} from "./lib";
import { PERIOD_OPTIONS, REPORT_PERMISSIONS } from "./options";

import s from "./RegMen.less";

const DEFAULT_SELECT_REPORT = "reportYear";

const RegMen = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const setData = useSetLocationData();
  const history = useHistory();
  const goBack = () => {
    history.replace("/dealers/");
  };

  // Ключи фильтра приходят из URL и не описаны в типе useGetLocationData
  const { SelectReport, dealerIdFilter, userIdFilter } =
    useGetLocationData() as Record<string, unknown>;

  const reportType = String(SelectReport || DEFAULT_SELECT_REPORT);

  const handleCloseModal = () => {
    setData("openModal", "");
  };
  const { notifyReport } = useNotify();
  const [getReportFileRegMenByDealerByDealerLazyQuery] =
    useGetReportFileRegMenByDealerByDealerLazyQuery({
      onCompleted(data) {
        const hash = get(
          data,
          `UserGetter.getReportFileRegMenByDealerByDealer.fileHash`,
        );
        hash && notifyReport(hash);
      },
      fetchPolicy: "no-cache",
    });
  // Отчёты «по периоду», «по рег. менеджеру» и сводный работают через REST API
  const {
    data: periodReport,
    isLoading: periodReportLoading,
    getRegMenByPeriodReport,
    resetRegMenByPeriodReport,
  } = useGetRegMenByPeriodReport();
  const {
    data: consolidateReport,
    isLoading: consolidateReportLoading,
    getRegMenConsolidateReport,
    resetRegMenConsolidateReport,
  } = useGetRegMenConsolidateReport();
  const { getRegMenByPeriodReportFile } = useGetRegMenByPeriodReportFile();
  const { getRegMenConsolidateReportFile } =
    useGetRegMenConsolidateReportFile(handleCloseModal);

  const [tableReport, setTableReport] = useState([]);

  const optionsReport = PERIOD_OPTIONS[reportType] || [];

  const [
    GetReportRegMenByDealerByDealerLazyQuery,
    ReportRegMenByDealerByDealerLazyQuery,
  ] = useGetReportRegMenByDealerByDealerLazyQuery({
    onCompleted(data) {
      const globals = get(
        data,
        "UserGetter.getReportRegMenByDealerByDealer",
        [],
      );
      setTableReport(globals);
    },
    fetchPolicy: "no-cache",
  });
  // Переменные отчёта по дилеру собираются из URL-фильтра
  const reportVariables = useMemo(() => {
    if (reportType === "reportDealer") {
      return { dealerId: dealerIdFilter ? String(dealerIdFilter) : undefined };
    }
    return {};
  }, [reportType, dealerIdFilter]);

  // Фильтр REST-отчёта по рег. менеджеру — выбранный в URL менеджер
  const regMenReportFilters = useMemo(
    () => ({
      regionalManagerId: userIdFilter ? Number(userIdFilter) : undefined,
    }),
    [userIdFilter],
  );

  // «Сформировать» — загрузка данных в таблицу
  const onViewReport = () => {
    if (reportType === "reportDealer") {
      GetReportRegMenByDealerByDealerLazyQuery({
        variables: reportVariables,
      });
    }
    if (reportType === "reportRegMen") {
      getRegMenByPeriodReport(regMenReportFilters);
    }
    if (reportType === "reportAll") {
      getRegMenConsolidateReport({});
    }
  };

  // «Сохранить в excel» — выгрузка файла отчёта
  const onDownloadReport = () => {
    if (reportType === "reportDealer") {
      getReportFileRegMenByDealerByDealerLazyQuery({
        variables: reportVariables,
      });
    }
    if (reportType === "reportRegMen") {
      getRegMenByPeriodReportFile(regMenReportFilters);
    }
    if (reportType === "reportAll") {
      getRegMenConsolidateReportFile({});
    }
  };

  useEffect(() => {
    setTableReport([]);
    resetRegMenByPeriodReport();
    resetRegMenConsolidateReport();
  }, [reportType]);

  const loading =
    periodReportLoading ||
    consolidateReportLoading ||
    ReportRegMenByDealerByDealerLazyQuery.loading;

  const isRestReport =
    reportType === "reportYear" || reportType === "reportAll";

  const lobof = tableReport.map((el) => {
    return {
      ...el,
      INN: el.INN ? el.INN : "",
    };
  });

  const canViewReport =
    !loadingPermissions && hasPermission(REPORT_PERMISSIONS[reportType]?.view);
  const canDownloadReport =
    !loadingPermissions && hasPermission(REPORT_PERMISSIONS[reportType]?.file);

  const periodRows = useMemo(
    () => mapPeriodRows(periodReport, t("regMen.temporaryShort")),
    [periodReport, t],
  );
  const consolidateRows = useMemo(
    () => mapConsolidateRows(consolidateReport),
    [consolidateReport],
  );

  // Формируем кастомные колонки для таблицы
  const columns = useMemo(() => {
    if (reportType === "reportYear") {
      const baseColumns = PERIOD_BASE_OPTIONS.map((option) => {
        // Ссылка на дилера — только в отчёте по периоду
        if (option === "DealerName") {
          return {
            title: t(`regMen.${option}`),
            dataIndex: "DealerName",
            key: "DealerName",
            render: (text: unknown, record: Record<string, any>) => (
              <Link to={`/dealers/${record.dealerId}/?selected=base`}>
                {String(text ?? "")}
              </Link>
            ),
          };
        }
        return {
          title: t(`regMen.${option}`),
          dataIndex: option,
          key: option,
          render: (text: unknown) => <DefaultTextCell text={text} />,
        };
      });
      const directionColumns = PERIOD_DIRECTION_COLUMNS.map(
        ({ key, titleKey }) => ({
          title: t(`regMen.${titleKey}`),
          dataIndex: key,
          key,
          render: (persons: unknown) => {
            const list = Array.isArray(persons) ? (persons as string[]) : [];
            if (list.length === 0) return <DefaultTextCell text="" />;
            return (
              <div className={s.person_list}>
                {list.map((name, index) => (
                  <div key={`${name}-${index}`}>{name}</div>
                ))}
              </div>
            );
          },
        }),
      );
      return [...baseColumns, ...directionColumns];
    }
    if (reportType === "reportAll") {
      const periodColumns = (consolidateReport?.periods ?? []).map(
        (period) => ({
          title: period,
          dataIndex: period,
          key: period,
          render: (text: unknown) => <DefaultTextCell text={text} />,
        }),
      );
      return [
        {
          title: t("regMen.regionalManager"),
          dataIndex: "regionalManager",
          key: "regionalManager",
          render: (text: unknown) => <DefaultTextCell text={text} />,
        },
        {
          title: t("regMen.role"),
          dataIndex: "role",
          key: "role",
          render: (text: unknown) => <DefaultTextCell text={text} />,
        },
        ...periodColumns,
      ];
    }
    return optionsReport.map((option) => ({
      title: t(`regMen.${option}`),
      dataIndex: option,
      key: option,
      render: (text: unknown) => <DefaultTextCell text={text} />,
    }));
  }, [optionsReport, reportType, consolidateReport, t]);

  const tableData: any[] =
    reportType === "reportYear" || reportType === "reportRegMen"
      ? periodRows
      : reportType === "reportAll"
        ? consolidateRows
        : lobof;

  return (
    <Wrapper.Content
      title={t("regMen.title")}
      filterComponent={<RegMenFilter />}
      txtBtn={t("ui.back")}
      goBackHandler={goBack}
    >
      {reportType === "reportYear" ? (
        <PeriodFilter
          canView={loadingPermissions || !!canViewReport}
          canDownload={loadingPermissions || !!canDownloadReport}
          onApply={getRegMenByPeriodReport}
          onGenerateFile={getRegMenByPeriodReportFile}
          onReset={resetRegMenByPeriodReport}
        />
      ) : (
        <Flex justify={"space-between"} className={s.actionsRow}>
          <Flex gap={10}>
            {(loadingPermissions || canViewReport) && (
              <Button
                color={"secondary"}
                variant={"filled"}
                bold
                fontSize="12"
                size={"middle"}
                radius={30}
                onClick={onViewReport}
              >
                {t("regMen.btnTable")}
              </Button>
            )}
            {(loadingPermissions || canDownloadReport) && (
              <Button
                color={"secondary"}
                variant={"filled"}
                bold
                fontSize="12"
                size={"middle"}
                radius={30}
                icon={<DownloadIcon size={16} fill="#495a64" />}
                onClick={onDownloadReport}
              >
                {t("editorQ.save_excel")}
              </Button>
            )}
          </Flex>
        </Flex>
      )}

      {loading ? (
        <Preloader />
      ) : (
        tableData.length > 0 && (
          <TableV2
            type="regMen"
            options={isRestReport ? undefined : optionsReport}
            columns={columns}
            dataSource={tableData.map((row, i) => ({ ...row, __rk: i }))}
            customRowsKey="__rk"
            loading={loading}
            wrapperClassName={s.dealers_table}
          />
        )
      )}
    </Wrapper.Content>
  );
};

export default RegMen;
